/**
 * 포인트 계열 쿼리 키 팩토리.
 *
 * 루트가 둘이고 **서로 무효화 대상이 아니다** — 잔액이 바뀐다고 순위표를 다시 받을 이유가
 * 없다(증빙 승인처럼 둘 다 바뀌는 작업은 두 키를 각각 무효화한다).
 *
 * 소비처가 `features/point` · `features/leaderboard` · `features/checkpoint-review` ·
 * `features/quest-proof` · `pages/MainPage` 로 **여러 슬라이스에 퍼져 있다.** features 끼리는
 * 서로 import 할 수 없으므로 `shared/api` 에 둔다 — `questKeys.ts` 와 같은 자리다.
 *
 * ⚠️ **정산**(`payouts`)은 여기 없다. 소비처가 `features/payout` 한 슬라이스뿐이라
 * 공용으로 올릴 이유가 없다 — `features/payout/model/payoutKeys.ts` 에 있다.
 *
 * 회귀 가드: `__tests__/pointKeys.test.ts`.
 */
import { defineQueryKeys } from '@/shared/lib/queryKeys';

export const pointBalanceKeys = defineQueryKeys('point-balance', (all) => ({
  byUser: (userId: string | undefined) => [...all, userId] as const,
}));

/**
 * 확정된 급여기간 배당 중 아직 안 나간 몫.
 *
 * 인자가 없다 — 기간·팀으로 좁히지 않고 미지급 전부를 받는다. 소비처는 재무 지급 탭이고
 * 확정 주체(팀장 예산 뷰)는 다른 슬라이스라, 확정 mutation 이 여기를 깨우려면 키가 공용이어야 한다.
 */
export const dividendShareKeys = defineQueryKeys('dividend-shares', (all) => ({
  payable: () => [...all, 'payable'] as const,
}));

/**
 * 포인트 이력.
 *
 * **세그먼트 순서를 고쳤다.** 예전 목록 키는 `['point-history', userId, type]` 이라
 * 퀘스트 합계(`['point-history', 'quest-total', …]`)와 **2번 칸을 나눠 썼다**
 * (§10-2 함정 ② — 가변값이 리터럴 자리를 차지). 실제 충돌은 없었다. uuid 가
 * `'quest-total'` 일 수는 없으니까. 다만 그 안전이 **타입이 아니라 우연**에 기대고 있었고,
 * "이 사용자 이력만 무효화" 같은 부분 무효화를 쓰려는 순간 두 뷰를 구별할 방법이 없었다.
 * `workLogs` 를 같은 이유로 같은 모양으로 고쳤다(§10-1-2).
 *
 * **계약(`defineInvalidation`)은 없다.** 무효화하는 3곳이 전부 루트 광역이라 선언할
 * scope 가 하나뿐이다 — 조건 두 개(§10-0) 중 첫째부터 안 선다. 부분 무효화가 실제로
 * 필요해지면 그때 얹는다.
 */
export const pointHistoryKeys = defineQueryKeys('point-history', (all) => ({
  list: (userId?: string, type?: string) => [...all, 'list', userId, type] as const,
  questTotal: (userId?: string, questId?: string) =>
    [...all, 'quest-total', userId, questId] as const,
  /** 내보내기 조회 — 화면 무한로드(`list`)와 다른 엔드포인트라 칸을 나눈다. */
  export: (from: string, to: string, types?: string[]) =>
    [...all, 'export', from, to, types?.join(',')] as const,
}));

/**
 * 순위표.
 *
 * ⚠️ `period` 와 리터럴 `'all'` 이 **같은 칸을 쓰는 것은 의도**다. 메인 화면의 전체 순위와
 * 기간 선택 순위가 같은 `GET /ranks/leaderboard` 를 부르는데, 예전에는 `['leaderboard']` 와
 * `['leaderboard','all']` 로 갈려 **같은 응답을 두 번** 받았다. 키를 맞춰 dedup 한 것이고
 * `useUserStatus.ts` 도크스트링에 그 경위가 남아 있다 — 세그먼트를 넣어 갈라놓지 말 것.
 */
export const leaderboardKeys = defineQueryKeys('leaderboard', (all) => ({
  byPeriod: (period: string) => [...all, period] as const,
}));
